import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Eye, 
  Lock, 
  MapPin, 
  Bot, 
  BookOpen, 
  Users, 
  ChevronRight,
  Zap,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';

export default function LandingPage() {
  const features = [
    {
      icon: Eye,
      title: 'Privacy Score',
      description: 'Get a real-time assessment of your digital privacy health with actionable insights.',
    },
    {
      icon: MapPin,
      title: 'Location Protection',
      description: 'Learn how to disable tracking and prevent apps from selling your location data.',
    },
    {
      icon: Lock,
      title: 'Security Toolkit',
      description: 'Access guides for VPNs, encrypted messaging, and password managers.',
    },
    {
      icon: Bot,
      title: 'AI Assistant',
      description: 'Get instant answers to your privacy questions from our AI-powered chatbot.',
    },
    {
      icon: BookOpen,
      title: 'Education Hub',
      description: 'Deep dive into privacy topics with comprehensive guides and tutorials.',
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Join discussions with privacy-conscious users and share knowledge.',
    },
  ];

  const stats = [
    { value: '250M+', label: 'Devices tracked by Fog Reveal' },
    { value: '$0', label: 'Cost for warrantless surveillance' },
    { value: '24/7', label: 'Your location is being logged' },
  ];

  return (
    <div className="relative" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-hero-gradient" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1770159116807-9b2a7bb82294?w=1920&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'brightness(0.3)',
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-4xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00FF94]/10 border border-[#00FF94]/20 rounded-full mb-8 animate-fade-in">
              <Zap className="w-4 h-4 text-[#00FF94]" />
              <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
                Counter Fog Reveal Surveillance
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-heading font-bold text-5xl sm:text-6xl lg:text-7xl text-white leading-tight mb-6 animate-slide-up">
              Take Back Your
              <span className="text-gradient block">Digital Privacy</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Government agencies are buying your location data without warrants. 
              Learn how to protect yourself from mass surveillance with our comprehensive privacy toolkit.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/register">
                <Button className="btn-primary h-12 px-8 text-base font-semibold group" data-testid="hero-cta-primary">
                  Start Protecting Yourself
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/education">
                <Button variant="outline" className="h-12 px-8 text-base border-[#262626] text-white hover:bg-white/5" data-testid="hero-cta-secondary">
                  Learn How It Works
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#262626] rounded-full flex items-start justify-center pt-2">
            <div className="w-1 h-2 bg-[#00FF94] rounded-full" />
          </div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-24 md:py-32 bg-[#050505] border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <span className="font-mono text-sm text-[#00FF94] uppercase tracking-widest mb-4 block">
                The Problem
              </span>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
                Your Phone is a Tracking Device
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                Fog Reveal and similar tools enable law enforcement to track "patterns of life" — 
                where you work, live, who you associate with, and what places you visit. 
                All without a warrant. All legal. All paid for with your tax dollars.
              </p>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center sm:text-left">
                    <div className="font-heading font-bold text-2xl sm:text-3xl text-[#00FF94] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500 font-mono uppercase">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="md:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-[#262626]">
                <img 
                  src="https://images.unsplash.com/photo-1586449480584-34302e933441?w=800&q=80"
                  alt="Data tracking visualization"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-sm text-[#00FF94] uppercase tracking-widest mb-4 block">
              Your Defense
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
              Everything You Need to Fight Back
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Comprehensive tools and knowledge to protect your digital privacy from surveillance.
            </p>
          </div>

          {/* Features Grid - Bento Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="group bg-[#050505] border border-[#262626] rounded-xl p-6 card-hover relative overflow-hidden"
                data-testid={`feature-card-${index}`}
              >
                {/* Hover gradient */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-b from-transparent to-[#00FF94]/5" />
                
                <div className="relative">
                  <div className="w-12 h-12 rounded-lg bg-[#00FF94]/10 border border-[#00FF94]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-6 h-6 text-[#00FF94]" />
                  </div>
                  <h3 className="font-heading font-semibold text-xl text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 md:py-32 bg-[#050505] border-t border-[#262626]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-sm text-[#00FF94] uppercase tracking-widest mb-4 block">
              How It Works
            </span>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white">
              Three Steps to Privacy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Assess Your Risk',
                description: 'Complete our privacy assessment to understand your current exposure level and get a personalized privacy score.',
              },
              {
                step: '02',
                title: 'Follow the Guides',
                description: 'Use our step-by-step guides to disable tracking, reset advertising IDs, and configure privacy settings.',
              },
              {
                step: '03',
                title: 'Stay Protected',
                description: 'Get ongoing recommendations, news updates, and community support to maintain your digital privacy.',
              },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="font-mono text-6xl font-bold text-[#00FF94]/10 mb-4">
                  {item.step}
                </div>
                <h3 className="font-heading font-semibold text-xl text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  {item.description}
                </p>
                {index < 2 && (
                  <ChevronRight className="hidden md:block absolute top-12 -right-4 w-8 h-8 text-[#262626]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-gradient opacity-50" />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00FF94]/10 border border-[#00FF94]/20 mb-8 animate-pulse-glow">
            <Shield className="w-8 h-8 text-[#00FF94]" />
          </div>
          
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-6">
            Ready to Protect Your Privacy?
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of users who have taken control of their digital privacy. 
            Start with our free plan and upgrade when you're ready.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button className="btn-primary h-12 px-8 text-base font-semibold" data-testid="cta-get-started">
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button variant="outline" className="h-12 px-8 text-base border-[#262626] text-white hover:bg-white/5" data-testid="cta-view-pricing">
                View Pricing
              </Button>
            </Link>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-500">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#00FF94]" />
              Free forever plan
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#00FF94]" />
              No credit card required
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
